import type { ScheduleTime } from '@src/core/buyerApiClient';
import { formatCents } from '@src/common';
import { collectTariffPrices, getTariffPriceForScheduleTime, ITariffPrice } from '@src/domains/tariffs';
import type { TariffsPerType } from '@src/stores/product';

export function getFormattedTariffPrice(
  tariffPrice: ITariffPrice,
  useOldPrice: boolean,
  priceToPrefix: string,
): string {
  if (useOldPrice) {
    return tariffPrice.oldPriceCents != null ? formatCents(tariffPrice.oldPriceCents) : '';
  }
  return (tariffPrice.oldPriceCents != null ? priceToPrefix + ' ' : '') + formatCents(tariffPrice.priceCents);
}

export function computeTariffPriceInfo(
  tariffPerType: TariffsPerType,
  selectedHour: ScheduleTime | undefined,
  useOldPrice: boolean,
  priceToPrefix: string,
): string {
  if (!tariffPerType.schedulings.length) {
    const currentType = tariffPerType.type;
    const tariff = tariffPerType.tariffGroup?.tariffs.find((t) => t.type.id === currentType.id);
    if (tariff) {
      return getFormattedTariffPrice(
        { priceCents: tariff.priceCents, oldPriceCents: tariff.oldPriceCents ?? null },
        useOldPrice,
        priceToPrefix,
      );
    }
  }

  if (selectedHour) {
    const tariff = getTariffPriceForScheduleTime(tariffPerType, selectedHour);
    if (tariff) {
      return getFormattedTariffPrice(tariff, useOldPrice, priceToPrefix);
    }
  }

  const possiblePrices = collectTariffPrices(tariffPerType);

  if (possiblePrices.length === 1) {
    return getFormattedTariffPrice(possiblePrices[0]!, useOldPrice, priceToPrefix);
  }

  const priceValues = possiblePrices
    .map<number | null | undefined>((t) => (useOldPrice ? t.oldPriceCents : t.priceCents))
    .filter((v) => v != null)
    .sort((a, b) => a! - b!);

  if (!priceValues.some((value) => value != null)) {
    return '';
  }

  const minCents = priceValues[0]!;
  const maxCents = priceValues[priceValues.length - 1]!;

  if (minCents === maxCents) {
    return formatCents(maxCents);
  }
  return `${formatCents(minCents)} - ${formatCents(maxCents)}`;
}
